import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Clock, Loader2, Save } from 'lucide-react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { organizationsAPI } from '../../lib/api';

/**
 * P_ORG_TIMEZONE_AWARE_SCHEDULING_1.0 — per-org scheduling profile editor.
 *
 * Exposes the four hour-of-day fields + IANA timezone that determine
 * when an organization's customer-facing communications fire. Backend
 * validates `timezone` against ZoneInfo, so invalid IANA names are
 * rejected with a 400 before we ever pollute the org doc.
 *
 * Presets cover the common North-American + EMEA + APAC zones a
 * super admin will reach for; an "Other (custom IANA)" escape hatch
 * keeps the world open if a tenant needs e.g. ``Pacific/Auckland``.
 */

const IANA_PRESETS = [
    { value: 'America/New_York', label: 'Eastern Time — America/New_York' },
    { value: 'America/Toronto', label: 'Eastern Time (Toronto) — America/Toronto' },
    { value: 'America/Chicago', label: 'Central Time — America/Chicago' },
    { value: 'America/Edmonton', label: 'Mountain Time — America/Edmonton' },
    { value: 'America/Denver', label: 'Mountain Time (Denver) — America/Denver' },
    { value: 'America/Vancouver', label: 'Pacific Time — America/Vancouver' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (LA) — America/Los_Angeles' },
    { value: 'Europe/London', label: 'United Kingdom — Europe/London' },
    { value: 'Europe/Paris', label: 'France — Europe/Paris' },
    { value: 'Australia/Sydney', label: 'Sydney — Australia/Sydney' },
];

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, h) => ({
    value: String(h),
    label: `${String(h).padStart(2, '0')}:00`,
}));

const SchedulingProfileEditor = ({ org, onSaved }) => {
    const { t } = useTranslation();
    const initial = {
        timezone: org.timezone || 'America/New_York',
        welcome_hour_local: org.welcome_hour_local ?? 8,
        voucher_hour_local: org.voucher_hour_local ?? 0,
        weekly_report_hour_local: org.weekly_report_hour_local ?? 8,
        recognition_hour_local: org.recognition_hour_local ?? 8,
    };
    const presetMatch = IANA_PRESETS.find((p) => p.value === initial.timezone);
    const [tzMode, setTzMode] = useState(presetMatch ? 'preset' : 'custom');
    const [form, setForm] = useState(initial);
    const [saving, setSaving] = useState(false);

    const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));
    const updateHour = (key, value) => update(key, Math.max(0, Math.min(23, Number(value))));

    const isDirty = (
        form.timezone !== initial.timezone ||
        form.welcome_hour_local !== initial.welcome_hour_local ||
        form.voucher_hour_local !== initial.voucher_hour_local ||
        form.weekly_report_hour_local !== initial.weekly_report_hour_local ||
        form.recognition_hour_local !== initial.recognition_hour_local
    );

    const handleSave = async () => {
        if (!isDirty || saving) return;
        setSaving(true);
        try {
            const payload = {
                timezone: form.timezone,
                welcome_hour_local: Number(form.welcome_hour_local),
                voucher_hour_local: Number(form.voucher_hour_local),
                weekly_report_hour_local: Number(form.weekly_report_hour_local),
                recognition_hour_local: Number(form.recognition_hour_local),
            };
            const res = await organizationsAPI.updateSchedulingProfile(org.id, payload);
            toast.success('Scheduling profile saved');
            if (onSaved) onSaved(res.data);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to save scheduling profile');
        } finally {
            setSaving(false);
        }
    };

    const tid = (s) => `org-${org.id}-${s}`;

    return (
        <div
            className="pt-3 border-t border-slate-100 dark:border-slate-700 space-y-3"
            data-testid={tid('scheduling-profile-row')}
        >
            <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-slate-500" />
                <span className="text-sm font-medium text-slate-700 dark:text-slate-200">
                    Scheduling profile
                </span>
                <span className="text-xs text-slate-500">
                    timezone + delivery hours
                </span>
            </div>

            {/* Timezone */}
            <div className="space-y-1.5">
                <Label className="text-xs text-slate-500 uppercase tracking-wide">Timezone (IANA)</Label>
                <Select
                    value={tzMode === 'preset' ? form.timezone : '__custom__'}
                    onValueChange={(v) => {
                        if (v === '__custom__') {
                            setTzMode('custom');
                        } else {
                            setTzMode('preset');
                            update('timezone', v);
                        }
                    }}
                >
                    <SelectTrigger className="w-full" data-testid={tid('tz-select')}>
                        <SelectValue placeholder="Select a timezone" />
                    </SelectTrigger>
                    <SelectContent>
                        {IANA_PRESETS.map((p) => (
                            <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                        ))}
                        <SelectItem value="__custom__">Other (custom IANA name)…</SelectItem>
                    </SelectContent>
                </Select>
                {tzMode === 'custom' ? (
                    <Input
                        value={form.timezone}
                        onChange={(e) => update('timezone', e.target.value)}
                        placeholder="e.g. Pacific/Auckland"
                        data-testid={tid('tz-custom')}
                    />
                ) : null}
            </div>

            {/* Hour fields */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {[
                    { key: 'welcome_hour_local', label: 'Welcome' },
                    { key: 'voucher_hour_local', label: 'Voucher Summary' },
                    { key: 'weekly_report_hour_local', label: t('common.weeklyReport') },
                    { key: 'recognition_hour_local', label: 'Recognition' },
                ].map(({ key, label }) => (
                    <div key={key} className="space-y-1">
                        <Label className="text-[11px] text-slate-500 uppercase tracking-wide">{label}</Label>
                        <Select
                            value={String(form[key])}
                            onValueChange={(v) => updateHour(key, v)}
                        >
                            <SelectTrigger data-testid={tid(`${key}-select`)}>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {HOUR_OPTIONS.map((o) => (
                                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                ))}
            </div>

            <div className="flex justify-end">
                <Button
                    size="sm"
                    onClick={handleSave}
                    disabled={!isDirty || saving}
                    data-testid={tid('scheduling-profile-save')}
                >
                    {saving ? (
                        <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Saving…</>
                    ) : (
                        <><Save className="w-3.5 h-3.5 mr-1.5" /> Save scheduling profile</>
                    )}
                </Button>
            </div>
        </div>
    );
};

export default SchedulingProfileEditor;
